import React from "react";
import * as styles from "web/react/components/feature-article/feature-article-blocks/feature-article-blocks.css";
import { FeatureArticleFaqs } from "web/react/components/feature-article/feature-article-faqs";
import { FeatureArticleFeaturedProducts } from "web/react/components/feature-article/feature-article-featured-products";
import { FeatureArticleImage } from "web/react/components/feature-article/feature-article-image";
import { FeatureArticleProductList } from "web/react/components/feature-article/feature-article-product-list";
import { FeatureArticleTable } from "web/react/components/feature-article/feature-article-table";
import { FeatureArticleText } from "web/react/components/feature-article/feature-article-text";
import { FeatureArticleTextBox } from "web/react/components/feature-article/feature-article-text-box";
import { FeatureArticleBlock } from "web/react/components/feature-article/types";

interface FeatureArticleBlocksProps {
    blocks: FeatureArticleBlock[];
    // Article-level sponsored flag (`meta.sponsored`) — forwarded to the product blocks so their
    // cards render the tracked lead links for advertorial content.
    isSponsored?: boolean;
}

/**
 * Renders the ordered list of content blocks (`body[]`) that make up a Feature Article,
 * dispatching each block to its component by `type`.
 * a type without a case yet renders nothing
 */
export function FeatureArticleBlocks({
    blocks,
    isSponsored = false,
}: FeatureArticleBlocksProps): React.ReactElement {
    return (
        <div className={styles.wrapper}>
            {blocks.map((block, index) => (
                <FeatureArticleBlockItem key={index} block={block} isSponsored={isSponsored} />
            ))}
        </div>
    );
}

function FeatureArticleBlockItem({
    block,
    isSponsored,
}: {
    block: FeatureArticleBlock;
    isSponsored: boolean;
}): React.ReactElement | null {
    switch (block.type) {
        case "text":
            return (
                <FeatureArticleText
                    heading={block.value.heading}
                    markdown={block.value.markdown ?? ""}
                />
            );
        case "image":
            return <FeatureArticleImage image={block.value} />;
        case "featured_products":
            return <FeatureArticleFeaturedProducts items={block.value} isSponsored={isSponsored} />;
        case "product_list":
            return <FeatureArticleProductList items={block.value} isSponsored={isSponsored} />;
        case "faqs":
            return (
                <FeatureArticleFaqs heading={block.value.heading ?? ""} faqs={block.value.faqs} />
            );
        case "table":
            return (
                <FeatureArticleTable
                    title={block.value.title}
                    subtitle={block.value.subtitle}
                    columnHeadings={block.value.table.column_headings.map(
                        (heading) => heading ?? ""
                    )}
                    rows={block.value.table.rows.map((row) => row.map((cell) => cell ?? ""))}
                    body={block.value.body}
                />
            );
        case "text_box":
            return (
                <FeatureArticleTextBox
                    heading={block.value.heading}
                    markdown={block.value.markdown ?? ""}
                />
            );
        default:
            return null;
    }
}
